/**
 * Cart drawer (snippets/cart-drawer.liquid). Public API used by product-form.js, product-card.js and
 * cart-recommendations.js: open(triggeredBy), close(), renderContents(parsedState), getSectionsToRender(),
 * setActiveElement(element). Focus is trapped in the dialog while open and returned to the trigger on
 * close; Esc and a click on the dimmed page close it.
 */
class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (evt) => {
      if (evt.target.id === 'CartDrawer-Overlay') this.close();
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (this.classList.contains('active')) {
      this.trapFocusInDrawer();
      return;
    }

    this.setNoteAccessibility();
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    let focused = false;
    const focusDrawer = () => {
      if (focused || !this.classList.contains('active')) return;
      focused = true;
      this.trapFocusInDrawer();
    };
    this.addEventListener('transitionend', focusDrawer, { once: true });
    // Fallback when no transition runs (e.g. reduced motion in some browsers).
    setTimeout(focusDrawer, 600);

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    if (!this.classList.contains('active')) return;
    this.classList.remove('active');

    // Return focus to whatever opened the drawer; if it is gone or hidden (e.g. a closed quick-add
    // panel), fall back to the header bag button.
    const trigger = this.activeElement;
    removeTrapFocus(trigger?.isConnected ? trigger : null);
    if (!trigger || document.activeElement !== trigger) document.getElementById('cart-icon-bubble')?.focus();
    document.body.classList.remove('overflow-hidden');
  }

  trapFocusInDrawer(elementToFocus) {
    const inner = this.querySelector('.drawer__inner');
    if (!inner) return;
    trapFocus(inner, elementToFocus || inner);
  }

  // The order note is a <details>: keep aria-expanded in sync and let Esc close the note before the drawer.
  setNoteAccessibility() {
    const details = this.querySelector('#Details-CartDrawer');
    const summary = details?.querySelector(':scope > summary');
    if (!summary || details.dataset.noteReady) return;
    details.dataset.noteReady = 'true';

    summary.setAttribute('role', 'button');
    summary.setAttribute('aria-expanded', String(details.open));
    if (summary.nextElementSibling?.id) summary.setAttribute('aria-controls', summary.nextElementSibling.id);

    details.addEventListener('toggle', () => {
      summary.setAttribute('aria-expanded', String(details.open));
    });
    // Capture phase: runs before global.js's onKeyUpEscape and the drawer's own Esc handler.
    details.addEventListener(
      'keyup',
      (event) => {
        if (event.code !== 'Escape' || !details.open) return;
        event.stopPropagation();
        details.open = false;
        summary.focus();
      },
      { capture: true }
    );
  }

  // Called after the drawer markup is replaced (renderContents, cart.js updates).
  contentsChanged() {
    this.classList.toggle('is-empty', !this.querySelector('#CartDrawer .cart-item'));
    this.setNoteAccessibility();
  }

  renderContents(parsedState) {
    const wasOpen = this.classList.contains('active');
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const html = parsedState.sections?.[section.id];

      if (!sectionElement || !html) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(html, section.selector);
    });

    this.contentsChanged();

    setTimeout(() => {
      if (wasOpen) {
        this.trapFocusInDrawer();
      } else {
        this.open();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'CartDrawer-LiveRegionText',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
